import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Avatar } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNetworkGraph } from '../api/queries';
import NetworkGraph from '../components/NetworkGraph/NetworkGraph';
import { NetworkNode } from '../api';
import { useHeader } from '../components/Layout/MainLayout';
import './Connections.css';

interface SelectedNode {
  id: string;
  type: 'musician' | 'band';
  name: string;
  photo_url: string | null;
  instruments?: string[];
  connectionCount: number;
  connectedEntities?: { id: string; name: string; type: string; photo_url?: string | null }[];
}

interface SidebarState {
  mode: 'top10' | 'connections';
  entityId?: string;
  entityName?: string;
  entityType?: 'musician' | 'band';
  items: { id: string; name: string; type: string; photo_url?: string | null; connectionCount?: number }[];
}

export type GraphTheme = 'dim' | 'normal' | 'trippy';

const Connections: React.FC = () => {
  const navigate = useNavigate();
  const { data: graphData, isLoading, error } = useNetworkGraph();
  const { searchQuery } = useHeader();
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [sidebarState, setSidebarState] = useState<SidebarState>({ mode: 'top10', items: [] });
  const [graphTheme, setGraphTheme] = useState<GraphTheme>('normal');

  // Get connected entities for a node with full details
  const getConnectedEntities = useCallback((nodeId: string) => {
    if (!graphData) return [];

    const connected: { id: string; name: string; type: string; photo_url?: string | null; connectionCount?: number }[] = [];

    graphData.links.forEach(link => {
      if (link.source === nodeId) {
        const targetNode = graphData.nodes.find(n => n.id === link.target);
        if (targetNode) {
          connected.push({
            id: targetNode.id,
            name: targetNode.name,
            type: targetNode.type,
            photo_url: targetNode.photo_url,
            connectionCount: targetNode.connectionCount,
          });
        }
      } else if (link.target === nodeId) {
        const sourceNode = graphData.nodes.find(n => n.id === link.source);
        if (sourceNode) {
          connected.push({
            id: sourceNode.id,
            name: sourceNode.name,
            type: sourceNode.type,
            photo_url: sourceNode.photo_url,
            connectionCount: sourceNode.connectionCount,
          });
        }
      }
    });

    return connected;
  }, [graphData]);

  // Handle node click from graph
  const handleNodeClick = useCallback((node: NetworkNode) => {
    const connectedEntities = getConnectedEntities(node.id);
    setSelectedNode({
      ...node,
      connectedEntities,
    });

    // Update sidebar to show connections
    setSidebarState({
      mode: 'connections',
      entityId: node.id,
      entityName: node.name,
      entityType: node.type,
      items: connectedEntities,
    });
  }, [getConnectedEntities]);

  // Handle sidebar item click - select that node and show its connections
  const handleSidebarItemClick = useCallback((item: { id: string; name: string; type: string; photo_url?: string | null; connectionCount?: number }) => {
    const node = graphData?.nodes.find(n => n.id === item.id);
    if (node) {
      const connectedEntities = getConnectedEntities(node.id);
      setSelectedNode({
        ...node,
        connectedEntities,
      });

      // Update sidebar to show this node's connections
      setSidebarState({
        mode: 'connections',
        entityId: node.id,
        entityName: node.name,
        entityType: node.type,
        items: connectedEntities,
      });
    }
  }, [graphData, getConnectedEntities]);

  // Go back to top 10
  const handleBackToTop10 = useCallback(() => {
    setSidebarState({ mode: 'top10', items: [] });
    setSelectedNode(null);
  }, []);

  // Navigate to entity detail page
  const handleEntityClick = useCallback((type: string, id: string) => {
    if (type === 'musician') {
      navigate(`/musicians/${id}`);
    } else if (type === 'band') {
      navigate(`/bands/${id}`);
    }
  }, [navigate]);

  // Close detail card
  const handleCloseDetail = useCallback(() => {
    setSelectedNode(null);
  }, []);

  if (isLoading) {
    return (
      <div className="connections-page connections-page--loading">
        <Spin size="large" />
        <p>Loading network...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="connections-page connections-page--error">
        <p>Failed to load network graph</p>
        <p className="connections-error-detail">{error.message}</p>
      </div>
    );
  }

  if (!graphData) {
    return null;
  }

  return (
    <div className="connections-page">
      {/* Graph container */}
      <div className="connections-graph">
        <NetworkGraph
          nodes={graphData.nodes}
          links={graphData.links}
          searchQuery={searchQuery}
          onNodeClick={handleNodeClick}
          selectedNodeId={selectedNode?.id}
          theme={graphTheme}
        />
      </div>

      {/* Sidebar */}
      <div className="connections-sidebar">
        {sidebarState.mode === 'top10' ? (
          <>
            <h3 className="connections-sidebar-title">TOP 10 MOST CONNECTED MUSICIANS</h3>
            <div className="connections-top-list">
              {graphData.topMusicians.map((musician, index) => (
                <div
                  key={musician.id}
                  className="connections-top-item"
                  onClick={() => handleSidebarItemClick({ ...musician, type: 'musician' })}
                >
                  <span className="connections-top-rank">{index + 1}</span>
                  <Avatar
                    src={musician.photo_url}
                    size={32}
                    className="connections-top-avatar"
                  >
                    {musician.name.charAt(0)}
                  </Avatar>
                  <span className="connections-top-name">{musician.name}</span>
                  <span className="connections-top-count">{musician.connectionCount}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button className="connections-sidebar-back" onClick={handleBackToTop10}>
              <ArrowLeftOutlined /> Back to Top 10
            </button>
            <h3 className="connections-sidebar-title">
              {sidebarState.entityType === 'musician' ? 'BANDS' : 'MEMBERS'}
            </h3>
            <div
              className="connections-sidebar-entity"
              onClick={() => sidebarState.entityId && sidebarState.entityType && handleEntityClick(sidebarState.entityType, sidebarState.entityId)}
            >
              <span className={`connections-sidebar-entity-dot connections-sidebar-entity-dot--${sidebarState.entityType}`} />
              <span className="connections-sidebar-entity-name">{sidebarState.entityName}</span>
            </div>
            <div className="connections-top-list">
              {sidebarState.items.map((item) => (
                <div
                  key={item.id}
                  className={`connections-top-item ${selectedNode?.id === item.id ? 'connections-top-item--selected' : ''}`}
                  onClick={() => handleSidebarItemClick(item)}
                >
                  <Avatar
                    src={item.photo_url}
                    size={32}
                    shape={item.type === 'band' ? 'square' : 'circle'}
                    className="connections-top-avatar"
                  >
                    {item.name.charAt(0)}
                  </Avatar>
                  <span className="connections-top-name">{item.name}</span>
                  <span className="connections-top-count">{item.connectionCount}</span>
                </div>
              ))}
              {sidebarState.items.length === 0 && (
                <div className="connections-sidebar-empty">No connections</div>
              )}
            </div>
          </>
        )}

        {/* Legend */}
        <div className="connections-legend">
          <div className="connections-legend-item">
            <span className="connections-legend-dot connections-legend-dot--musician" />
            <span>musician</span>
          </div>
          <div className="connections-legend-item">
            <span className="connections-legend-dot connections-legend-dot--band" />
            <span>band</span>
          </div>
        </div>
      </div>

      {/* Selected node detail card */}
      {selectedNode && (
        <div className="connections-detail-card">
          <button className="connections-detail-close" onClick={handleCloseDetail}>
            ×
          </button>
          <div className="connections-detail-header">
            <Avatar
              src={selectedNode.photo_url}
              size={48}
              shape={selectedNode.type === 'band' ? 'square' : 'circle'}
              className="connections-detail-avatar"
            >
              {selectedNode.name.charAt(0)}
            </Avatar>
            <div className="connections-detail-info">
              <h4
                className="connections-detail-name"
                onClick={() => handleEntityClick(selectedNode.type, selectedNode.id)}
              >
                {selectedNode.name}
              </h4>
              <p className="connections-detail-meta">
                {selectedNode.type} · {selectedNode.connectionCount} {selectedNode.type === 'musician' ? 'bands' : 'members'}
              </p>
            </div>
          </div>

          {selectedNode.instruments && selectedNode.instruments.length > 0 && (
            <p className="connections-detail-instruments">
              ♪ {selectedNode.instruments.join(' · ')}
            </p>
          )}

          {selectedNode.connectedEntities && selectedNode.connectedEntities.length > 0 && (
            <div className="connections-detail-connected">
              {selectedNode.connectedEntities.map(entity => (
                <span
                  key={entity.id}
                  className={`connections-detail-tag connections-detail-tag--${entity.type}`}
                  onClick={() => handleEntityClick(entity.type, entity.id)}
                >
                  • {entity.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats bar */}
      <div className="connections-stats">
        <span>{graphData.stats.musicianCount} musicians</span>
        <span className="connections-stats-divider">·</span>
        <span>{graphData.stats.bandCount} bands</span>
        <span className="connections-stats-divider">·</span>
        <span>{graphData.stats.connectionCount} connections</span>
      </div>

      {/* Theme selector */}
      <button
        className="connections-theme-button"
        onClick={() => {
          const themes: GraphTheme[] = ['normal', 'dim', 'trippy'];
          const currentIndex = themes.indexOf(graphTheme);
          const nextIndex = (currentIndex + 1) % themes.length;
          setGraphTheme(themes[nextIndex]);
        }}
      >
        {graphTheme}
      </button>
    </div>
  );
};

export default Connections;
